import { FiShield } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { RfxLead } from "newui/features/Rfx/types/rfx.types";
import { RfxLeadInfoBox } from "../steps/InitialEvaluationStep/panel";
import { ConflictPendingReviewNotice } from "../ConflictPendingReviewNotice";
import { PanelMemberBlockedReason } from "../../types/evaluation.type";

type RfxLeadInfo = RfxLead;

interface EvaluationAccessBoundaryProps {
	children: React.ReactNode;
	error: unknown;
	rfxLead?: RfxLeadInfo;
	scoringBlockedReason?: PanelMemberBlockedReason | null;
}

const isAxios403 = (error: unknown): boolean => {
	if (!error || typeof error !== "object") {
		return false;
	}
	const err = error as { response?: { status?: number }; status?: number };
	return err.response?.status === 403 || err.status === 403;
};

/**
 * Access-denied reason codes returned in the 403 response body (PRO-579 / Task 001).
 * The envelope differs by layer (middleware: `{ success, message, reason }`,
 * controller: `{ status, title, message, reason }`), but both expose `reason` at
 * the top level of `response.data`, so we switch on that.
 */
type EvaluationAccessReason = "not_panel_member" | "cross_company" | "revoked";

const ACCESS_DENIED_MESSAGES: Record<EvaluationAccessReason, string> = {
	not_panel_member: "You are not a panel member for this evaluation.",
	cross_company: "You do not have access to this evaluation.",
	revoked: "Your access to this evaluation has been revoked.",
};

const GENERIC_ACCESS_DENIED_MESSAGE =
	"Your access to this evaluation is denied. If you believe this is an error, please contact the RFx Lead.";

const getAxios403Reason = (
	error: unknown
): EvaluationAccessReason | undefined => {
	if (!error || typeof error !== "object") {
		return undefined;
	}
	const err = error as { response?: { data?: { reason?: string } } };
	const reason = err.response?.data?.reason;
	if (
		reason === "not_panel_member" ||
		reason === "cross_company" ||
		reason === "revoked"
	) {
		return reason;
	}
	return undefined;
};

interface RevokedScreenProps {
	rfxLead?: RfxLeadInfo;
	scoringBlockedReason?: PanelMemberBlockedReason | undefined;
	accessReason?: EvaluationAccessReason | undefined;
}

const RevokedScreen = ({ scoringBlockedReason, accessReason, rfxLead }: RevokedScreenProps) => {
	const handleGoBack = (): void => {
		window.history.back();
	};

	return (
		<div
			data-testid="evaluation-access-revoked"
			className="flex min-h-[60vh] items-center justify-center p-6"
		>
			<div className="flex max-w-lg flex-col items-center rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
				<div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
					<FiShield
						className="h-6 w-6 text-amber-600"
						data-testid="evaluation-access-revoked-icon"
						aria-hidden="true"
					/>
				</div>
				<h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
				{!scoringBlockedReason && (
					<p
						data-testid="evaluation-access-reason"
						className="mt-2 text-sm text-gray-500"
					>
						{accessReason
							? ACCESS_DENIED_MESSAGES[accessReason]
							: GENERIC_ACCESS_DENIED_MESSAGE}
					</p>
				)}
				{rfxLead && (
					<div className="mt-4 w-full space-y-4">
						{scoringBlockedReason === "conflict_pending_review" && <ConflictPendingReviewNotice hideLeadInfo rfxLead={rfxLead} />}
						{scoringBlockedReason === "declined" && <p>You declined to participate in this evaluation.</p>}
						<RfxLeadInfoBox rfxLead={rfxLead} />
					</div>
				)}
				<Button
					btntype="secondary"
					onClick={handleGoBack}
					data-testid="evaluation-access-revoked-back"
					className="mt-6"
				>
					Go Back
				</Button>
			</div>
		</div>
	);
};

export const EvaluationAccessBoundary = ({
	error,
	rfxLead,
	scoringBlockedReason,
	children,
}: EvaluationAccessBoundaryProps) => {
	const is403 = isAxios403(error);
	if (is403 || scoringBlockedReason) {
		const accessReason = is403 ? getAxios403Reason(error) : undefined;
		return (
			<RevokedScreen
				scoringBlockedReason={scoringBlockedReason}
				accessReason={accessReason}
				rfxLead={rfxLead}
			/>
		);
	}
	return <>{children}</>;
};
